// Панель общего множителя: перекидной счётчик справа от мини-слота, см. GDD, раздел 5.2.
//
// Счётчик показывает сумму трёх манометров. На неё бонус Steam Spin умножает выигрыш
// мини-слота, см. GDD, раздел 5.3.
//
// Смена значения перекидывает карточку с числом и разом зажигает знак умножения слева
// от неё. Обе части — одна анимация скелета, поэтому игре достаточно её пустить.
//
// Число на карточке рисует игра. В атласе панели на его месте лежит образец художника —
// два числа на две карточки, — а цифр нет вовсе. Поэтому игра набирает число на канвасе
// и ставит его картинку вместо образца, см. assets/paintedSlot.ts. Половинки карточки берут
// эту картинку обе: верхняя показывает верх числа, нижняя — низ, а разрез идёт по середине цифр.
//
// Ход кончается своим последним кадром, а не позой покоя. Число в них одно и то же, но тень
// упавшей створки в них лежит на разных половинках карточки, и переход между ними — заметный
// скачок. Поза покоя нужна только там, где ход не идёт: на загрузке и на скипе. Новый ход
// поднимает створку сам, своим первым кадром.
//
// Координаты здесь те же, что у барабанов: это пиксели картинки барабанов.

import { CanvasSource, Container, type TextureSource } from 'pixi.js';

import { COUNTER_FONT, TOTAL_METER_CARD, TOTAL_METER_CARDS, TOTAL_METER_SKELETON } from '../../assets/catalog';
import type { AssetManager, Spine } from '../../assets/manager';
import { PaintedSlot } from '../../assets/paintedSlot';
import { animationSeconds, placeSpine, poseSpine } from '../../ui/scene';

/**
 * Во сколько раз канвас числа крупнее самой карточки.
 *
 * Запас взят на приближение взгляда: на время бонуса игра подходит к мини-слоту ближе,
 * и карточка растёт вместе с ним, см. ui/camera.ts.
 */
const CARD_SCALE: number = 2;

/** Какую долю высоты карточки занимает цифра. */
const DIGIT_HEIGHT: number = 0.62;

/**
 * Кегль, которым цифры замеряются перед набором.
 *
 * Кегль шрифта и высота цифр — разные величины, и отношение между ними у каждого шрифта своё:
 * у Bebas Neue цифра занимает почти весь кегль, у шрифта с крупными выносными — куда меньше.
 * Поэтому нужный кегль считается замером, а само это число на итог не влияет.
 */
const PROBE_SIZE: number = 100;

/** Цвет цифр. */
const DIGIT_COLOR: string = '#151515';

/** Цвет обводки и её толщина в долях высоты цифры. Обводка отделяет число от тени на карточке. */
const EDGE_COLOR: string = '#f4f0e6';
const EDGE_WIDTH: number = 0.05;

/** Запись числа на счётчике: две цифры, как на образце художника в ассете. */
export function counterText(value: number): string {
  return Math.max(0, Math.round(value)).toString().padStart(2, '0');
}

/**
 * Высота набранных цифр по замеру.
 *
 * Замер идёт по самим цифрам, а не по кеглю. Браузер без этого замера отдаёт нули,
 * и тогда высотой считается кегль: цифры выйдут мельче, но на месте.
 */
function drawnHeight(drawn: TextMetrics, size: number): number {
  const height: number = drawn.actualBoundingBoxAscent + drawn.actualBoundingBoxDescent;

  return height > 0 ? height : size;
}

/**
 * Набирает картинку числа под карточку счётчика.
 *
 * Цифры стоят серединой на разрезе карточки: по нему её перекидывает анимация, и разрез
 * должен идти по середине цифр, а не по их краю.
 *
 * @throws Error если канвас не отдал контекст 2d
 */
function numberSource(value: number, family: string): TextureSource {
  const canvas: HTMLCanvasElement = document.createElement('canvas');

  canvas.width = TOTAL_METER_CARD.width * CARD_SCALE;
  canvas.height = TOTAL_METER_CARD.height * CARD_SCALE;

  const paper: CanvasRenderingContext2D | null = canvas.getContext('2d');
  if (paper === null)
    throw new Error('Канвас не отдал контекст 2d: счётчику нечем набрать число');

  const text: string = counterText(value);
  const height: number = canvas.height * DIGIT_HEIGHT;

  paper.textAlign = 'center';
  paper.textBaseline = 'alphabetic';

  // Кегль подбирается замером: цифры набираются пробным кеглем, а потом он приводится
  // к нужной высоте.
  paper.font = `${PROBE_SIZE}px "${family}"`;
  const size: number = (PROBE_SIZE * height) / drawnHeight(paper.measureText(text), PROBE_SIZE);

  paper.font = `${size}px "${family}"`;

  // Середина считается по самим цифрам, а не по кеглю: поля кегля сверху и снизу у каждого
  // шрифта свои, и по ним разрез прошёл бы мимо середины цифр.
  const drawn: TextMetrics = paper.measureText(text);
  const x: number = canvas.width / 2;
  const y: number = canvas.height * TOTAL_METER_CARD.split + (drawn.actualBoundingBoxAscent - drawn.actualBoundingBoxDescent) / 2;

  paper.lineJoin = 'round';
  paper.lineWidth = size * EDGE_WIDTH;
  paper.strokeStyle = EDGE_COLOR;
  paper.strokeText(text, x, y);

  paper.fillStyle = DIGIT_COLOR;
  paper.fillText(text, x, y);

  return new CanvasSource({ resource: canvas });
}

/** Панель общего множителя: счётчик справа от мини-слота. */
export class TotalMultiplier {
  readonly view: Container = new Container();

  readonly #spine: Spine;
  readonly #font: string;

  /** Половинки видимой карточки и той, что выезжает ей на смену. */
  readonly #current: PaintedSlot[];
  readonly #next: PaintedSlot[];

  /** Картинки чисел. Число набирается один раз: за игру их набирается три десятка. */
  readonly #numbers = new Map<number, TextureSource>();

  /** Значение, которое счётчик показывает или к которому перекидывается. */
  #value = 0;

  /** Сколько ходу осталось идти. Ноль — счётчик стоит. */
  #left = 0;

  /** Кого разбудить, когда ход кончится. */
  #done: (() => void) | null = null;

  /**
   * @param x Середина панели по горизонтали в координатах картинки барабанов.
   * @param y Середина панели по вертикали там же.
   * @throws Error если шрифт или скелет не загружены
   */
  constructor(assets: AssetManager, x: number, y: number) {
    this.#font = assets.font(COUNTER_FONT);

    const spine: Spine = assets.spine(TOTAL_METER_SKELETON);

    // Панель нарисована в тех же пикселях, что и картинка барабанов, поэтому масштаб единичный.
    placeSpine(spine, 1, x, y);

    this.#spine = spine;
    this.#current = TOTAL_METER_CARDS.current.map((name: string) => new PaintedSlot(spine, name));
    this.#next = TOTAL_METER_CARDS.next.map((name: string) => new PaintedSlot(spine, name));

    this.view.addChild(spine);

    this.#rest(0);
  }

  /**
   * Ставит значение на счётчик без хода.
   *
   * Так игра показывает состояние сессии на загрузке: сумма набрана прошлыми раундами,
   * и перекидывать её на глазах у игрока нечего.
   */
  show(value: number): void {
    // Счётчик уже стоит на этом числе: трогать его нечем. Иначе панель дёрнулась бы из
    // последнего кадра хода в позу покоя, хотя число в них одно и то же.
    if (value === this.#value && this.#left <= 0)
      return;

    this.#rest(value);
  }

  /**
   * Перекидывает счётчик на новое значение и ждёт конца хода.
   *
   * Ход на месте пуст: то же значение перекидывать незачем.
   */
  set(value: number): Promise<void> {
    if (value === this.#value)
      return Promise.resolve();

    // Ход, который не досмотрели, кончается здесь: новый идёт от его значения.
    this.#wake();

    // Число уезжает на ту пару половинок, которую покажет первый кадр хода. Он же поднимает
    // створку и возвращает её тень наверх, поэтому позу покоя перед ходом ставить нечем.
    this.#draw(this.#current, this.#value);
    this.#draw(this.#next, value);

    this.#value = value;

    const flip: string = TOTAL_METER_SKELETON.animations.flip;

    this.#spine.state.setAnimation(0, flip, false);
    this.#left = animationSeconds(this.#spine, flip);

    return new Promise<void>((resolve) => {
      // Ход кончает либо сам счётчик, либо скип: брошенное ожидание не кончилось бы никогда.
      this.#done = resolve;
    });
  }

  /** Ставит счётчик на его значение разом: игрок торопит показ. */
  skip(): void {
    this.#rest(this.#value);
  }

  update(seconds: number): void {
    if (this.#left <= 0)
      return;

    this.#left -= seconds;

    if (this.#left > 0)
      return;

    // Ход остаётся стоять в своём последнем кадре: он и есть вид счётчика между ходами.
    // Обрывать его позой покоя нечем, а следующий ход поднимет створку сам.
    this.#left = 0;
    this.#wake();
  }

  /** Ставит счётчик в позу покоя на заданном числе. */
  #rest(value: number): void {
    this.#value = value;
    this.#left = 0;

    // Обе карточки берут одно число: та, что позади, всё равно не видна, а после хода
    // впереди окажется как раз она.
    this.#draw(this.#current, value);
    this.#draw(this.#next, value);

    poseSpine(this.#spine, TOTAL_METER_SKELETON.animations.idle);

    this.#wake();
  }

  /** Ставит число на обе половинки карточки. */
  #draw(card: readonly PaintedSlot[], value: number): void {
    const number: TextureSource = this.#number(value);

    for (const half of card)
      half.paint(number);
  }

  /** Картинка числа. Набранная однажды, она остаётся в запасе на всю игру. */
  #number(value: number): TextureSource {
    const ready: TextureSource | undefined = this.#numbers.get(value);
    if (ready !== undefined)
      return ready;

    const drawn: TextureSource = numberSource(value, this.#font);
    this.#numbers.set(value, drawn);

    return drawn;
  }

  /** Будит того, кто ждёт ход. Второй раз будить некого. */
  #wake(): void {
    const done: (() => void) | null = this.#done;

    this.#done = null;
    done?.();
  }
}
